<template>
  <div :class="name">
    <br />
    <div class="pr-2">
      <v-breadcrumbs :items="breadcrumbs">
        <template v-slot:divider>
          <v-icon>chevron_right</v-icon>
        </template>
      </v-breadcrumbs>
    </div>
    <div v-if="$router.currentRoute.name === 'transportation-account'" class="pa-2">
<!--      <v-progress-linear intermediate v-show="apiInProgress" height="4"></v-progress-linear>-->
      <v-form v-if="currentAccount && !currentAccountError" @submit.prevent="save()" ref="form">
        <div class="layout justify-start" style="overflow-y: auto" v-if="currentAccount.id">
          <v-btn v-if="hasUserType('admin') || hasTransportationRole('OWNER') || hasTransportationRole('ADMIN')" :disabled="isOnlyInvestor" :to="{name: 'transportation-accounts-payouts-schedule', params: { accountId }}" class="mr-2">
            <v-icon small>fal fa-calendar-day</v-icon> &nbsp; Payout scheduler
          </v-btn>
          <v-btn v-if="hasUserType('admin') || hasTransportationRole('OWNER') || hasTransportationRole('ADMIN')" :disabled="isOnlyInvestor" :to="{name: 'transportation-accounts-payouts-payout', params: { payoutId: '0', accountId }}" class="mr-2">
            <v-icon small>fal fa-envelope-open-dollar</v-icon> &nbsp; Make a payout
          </v-btn>
          <v-btn class="mr-2" :disabled="apiInProgress || payoutSettingsApiInProgress" v-if="canViewPayoutSettings" @click="openPayoutSettingsDialog()">
            <v-icon small>fal fa-money-check-edit-alt</v-icon> &nbsp; Payout settings
          </v-btn>
          <v-btn color="mr-2" :to="{name: 'transportation-accounts-vehicles', params: {accountId}}" :disabled="apiInProgress"><v-icon small>fal fa-bus</v-icon> &nbsp; Vehicles</v-btn>
          <v-btn :to="{name: 'transportation-accounts-users', params: {accountId}}" :disabled="apiInProgress"><v-icon small>fal fa-address-card</v-icon> &nbsp; Drivers</v-btn>
        </div>
        <div class="layout justify-end">
          <v-btn color="warning mr-2" flat @click.stop="loadParentView" :disabled="apiInProgress">Cancel</v-btn> <v-btn color="primary" type="submit" v-if="hasUserType('admin') || hasTransportationRole('OWNER')" :disabled="apiInProgress" :loading="apiInProgress">Save</v-btn>
        </div>
        <v-layout row wrap>
          <v-flex sm6 pa-2>
            <div>
              <v-text-field v-model="currentAccount.companyName" pb-2 required :readonly="!hasUserType('admin') && !hasTransportationRole('OWNER')" label="Company name"></v-text-field>
              <v-chip
                v-if="showAnonymizedAccountName(currentAccount)"
                small
                class="mt-0 ml-0"
                title="Anonymized account name shown to stakeholders"
              >
                <small>{{ currentAccount.anonymizedCompanyName }}</small>
              </v-chip>
            </div>
          </v-flex>
          <v-flex sm6 pa-2>
            <v-text-field v-model="currentAccount.contactName" pb-2 required :readonly="!hasUserType('admin') && !hasTransportationRole('OWNER')" label="Contact name"></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row wrap v-if="!currentAccount.id">
          <v-flex sm6 pa-2>
            <v-select
              outline
              required
              :items="[{text: 'Phone', value: 'phone'}, {text: 'Email', value: 'email'}]"
              :disabled="!currentAccount.verificationNotice"
              :label="'Verification Type' + (!currentAccount.verificationNotice ? ' (enable \'send verification\' to use)' : '')"
              v-model="currentAccount.verificationType"
            />
          </v-flex>
          <v-flex sm6 pa-2 class="layout justify-start align-baseline">
            <v-tooltip top max-width="400">
              <template v-slot:activator="{ on }">
                <v-btn flat icon><v-icon v-on="on">fal fa-info-circle</v-icon></v-btn>
              </template>
              <span>A default "owner" user is created with a new account. Enable this option to have the user receive a verification email/text message when the account is created, so that they can set their own password. If no verification is sent, the user can always submit a "forgot password" reset request .</span>
            </v-tooltip>
            <v-switch v-model="currentAccount.verificationNotice" label="Send verification to the contact of this account" />
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm6 pa-2>
          <v-text-field
              pb-2
              :box="currentAccount.verificationType === 'email'"
              :required="!currentAccount.contactPhone"
              :readonly="!hasUserType('admin') && !hasTransportationRole('OWNER')"
              type="email" v-model="currentAccount.contactEmail"
              :rules="[(v) => (!v || /^[^@]+@[^@]+\.[0-9a-zA-Z]{2,}$/.test(v) || 'Valid email format required')]"
              :label="'Contact email address' + (currentAccount.verificationType === 'phone' ? ' (optional)' : '')">
          </v-text-field>
          </v-flex>
          <v-flex sm6 pa-2>
            <v-text-field
                pb-2
                readonly
                v-if="!hasUserType('admin') && !hasTransportationRole('OWNER')"
                v-model="currentAccount.contactPhone"
                label="Contact phone number">
            </v-text-field>
            <phone-number-field
              pb-2
              v-if="hasUserType('admin') || hasTransportationRole('OWNER')"
              v-model="currentAccount.contactPhone"
              :child-props="{ box: currentAccount.verificationType === 'phone', label: 'Contact phone number' + (currentAccount.verificationType === 'email' ? ' (optional)' : '') }"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm6 pa-2>
            <v-select
              outline
              required
              :items="countries"
              :disabled="!hasUserType('admin') && !hasTransportationRole('OWNER')"
              label="Country (location vehicles will be operating in)"
              :value="selectedCountry"
              @change="onFilterByCountry"
            />
          </v-flex>
          <v-flex sm6 pa-2 class="layout justify-start align-baseline">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn flat icon><v-icon v-on="on">fal fa-info-circle</v-icon></v-btn>
              </template>
              <span>Registered tablet devices (Equipment tokens) determine how many buses can have a registered tablet device</span>
            </v-tooltip>
            <v-text-field
                pb-2
                type="number"
                :readonly="!hasUserType('admin')"
                v-model="currentAccount.equipmentTokens"
                :rules="[(v) => (!v || /^\d+$/.test(v) || 'Valid number required')]"
                label="Registered tablet devices allowed">
            </v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row wrap v-if="!currentAccount.id">
          <v-flex sm6 pa-2>
            <div>
              <v-switch v-model="currentAccount.registerMobileMoney" class="mb-2" label="Register mobile money account" />
            </div>
            <div v-show="currentAccount.registerMobileMoney">
              <v-switch v-model="currentAccount.mobileMoneyPhoneIsNotContactPhone" class="mb-2" label="Mobile money phone is different from contact phone" />
              <div v-show="currentAccount.mobileMoneyPhoneIsNotContactPhone">
                <phone-number-field
                  v-model="currentAccount.mobileMoneyPhoneNumber"
                  :child-props="{ label: 'Mobile money phone number' }"
                />
              </div>
            </div>
          </v-flex>
          <v-flex sm6 pa-2>
            <div v-show="currentAccount.registerMobileMoney">
              <v-select
                pb-2
                outline
                clearable
                :items="[{ value: 'mtnghana', text: 'MTN Mobile Money' }, { value: 'atigoghana', text: 'Airtel/Tigo Money' }, { value: 'vodaghana', text: 'Telecel Cash' }]"
                v-model="currentAccount.moneyAccountTypeId"
                label="Mobile money account"
              />
            </div>
            <div class="mt-2 layout justify-start align-baseline">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn style="margin: 0" flat icon><v-icon v-on="on">fal fa-info-circle</v-icon></v-btn>
                </template>
                <span>Will not display the SmartTransit fee deduction in the SMS/email<br />
                payout report sent to the account owner</span>
              </v-tooltip>
              <v-switch v-model="currentAccount.excludeFeeLabel" class="ml-2" label="Exclude deduction fee in payout report" />
            </div>
          </v-flex>
        </v-layout>
        <v-layout row wrap v-if="isAdmin && isPendingVerification">
          <v-flex sm6 pa-2>
            <v-btn small :disabled="resendingInvitation" :loading="resendingInvitation" @click="resendInvitation()"><v-icon left small>fas fa-envelope</v-icon> Resend Invitation</v-btn>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm6 pa-2 v-show="currentAccount.id && currentAccount.dateCreatedLabel">
            Date created: {{currentAccount.dateCreatedLabel}}
          </v-flex>
          <v-flex sm6 pa-2 v-show="currentAccount.dateUpdatedLabel">
            Date updated: {{currentAccount.dateUpdatedLabel}}
          </v-flex>
        </v-layout>
        <div class="layout justify-end">
          <v-btn color="warning mr-2" flat @click.stop="loadParentView" :disabled="apiInProgress">Cancel</v-btn> <v-btn color="primary" type="submit" v-if="hasUserType('admin') || hasTransportationRole('OWNER')" :disabled="apiInProgress" :loading="apiInProgress">Save</v-btn>
        </div>
      </v-form>
      <v-alert
        :value="currentAccountError"
        type="error"
      >
        <p>{{currentAccountError}}</p>
        <p><router-link :to="{ name: 'transportation-account', params: { accountId: accountId } }">Retry</router-link> or <router-link :to="{ name: 'transportation-accounts' }">Return to accounts list</router-link></p>
      </v-alert>
    </div>
    <v-dialog
      transition="dialog-bottom-transition"
      scrollable
      persistent
      max-width="680"
      v-model="payoutSettingsDialog"
    >
      <v-card v-if="payoutSettingsForm">
        <v-card-title class="headline grey lighten-2 text-xs-center" primary-title>
          Account Payout Settings
        </v-card-title>
        <v-card-text>
          <v-alert :value="true" :type="hasPayoutSettingsOverride ? 'info' : 'warning'" class="mb-3">
            {{ hasPayoutSettingsOverride ? 'Using account-level override values for payouts.' : 'No account override found. This account is using global payout settings.' }}
          </v-alert>

          <v-layout row wrap>
            <v-flex xs12 pa-1>
              <v-select
                v-model="payoutSettingsForm.agencyId"
                :items="payoutAgencyOptions"
                item-text="text"
                item-value="value"
                label="Agency ID"
                disabled
              />
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.smarttransytFee }}</span>
                </v-tooltip>
                <currency-field
                  v-model.number="payoutSettingsForm.smarttransytFee"
                  :child-props="{ label: payoutFieldLabels.smarttransytFee, min: 0, placeholder: 'inherit global settings', disabled: !canEditPayoutSettings || payoutSettingsApiInProgress }"
                />
              </div>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.smarttransytFeePercentage }}</span>
                </v-tooltip>
                <v-text-field
                  v-model.number="payoutSettingsForm.smarttransytFeePercentage"
                  :label="payoutFieldLabels.smarttransytFeePercentage"
                  placeholder="inherit global settings"
                  type="number"
                  min="0"
                  step="1"
                  suffix="%"
                  :disabled="!canEditPayoutSettings || payoutSettingsApiInProgress"
                />
              </div>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.beaconFee }}</span>
                </v-tooltip>
                <currency-field
                  v-model.number="payoutSettingsForm.beaconFee"
                  :child-props="{ label: payoutFieldLabels.beaconFee, min: 0, placeholder: 'inherit global settings', disabled: !canEditPayoutSettings || payoutSettingsApiInProgress }"
                />
              </div>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.beaconMaintenanceFee }}</span>
                </v-tooltip>
                <currency-field
                  v-model.number="payoutSettingsForm.beaconMaintenanceFee"
                  :child-props="{ label: payoutFieldLabels.beaconMaintenanceFee, min: 0, placeholder: 'inherit global settings', disabled: !canEditPayoutSettings || payoutSettingsApiInProgress }"
                />
              </div>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.perDayDriverFee }}</span>
                </v-tooltip>
                <currency-field
                  v-model.number="payoutSettingsForm.perDayDriverFee"
                  :child-props="{ label: payoutFieldLabels.perDayDriverFee, min: 0, placeholder: 'inherit global settings', disabled: !canEditPayoutSettings || payoutSettingsApiInProgress }"
                />
              </div>
            </v-flex>
            <v-flex xs12 sm6 pa-1>
              <div class="layout align-start mb-1">
                <v-tooltip top max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-btn icon small class="ma-0 ml-1" v-on="on">
                      <v-icon small>fa fa-info-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ payoutFieldTooltips.perKwHFee }}</span>
                </v-tooltip>
                <currency-field
                  v-model.number="payoutSettingsForm.perKwHFee"
                  :child-props="{ label: payoutFieldLabels.perKwHFee, min: 0, placeholder: 'inherit global settings', disabled: !canEditPayoutSettings || payoutSettingsApiInProgress }"
                />
              </div>
            </v-flex>
          </v-layout>

          <div v-if="payoutSettingsGlobal" class="mt-2">
            <small>
              Global defaults:
              smarttransytFee={{ formatCurrencyFromCents(payoutSettingsGlobal.smarttransytFee) }},
              smarttransytFeePercentage={{ payoutSettingsGlobal.smarttransytFeePercentage }},
              beaconFee={{ formatCurrencyFromCents(payoutSettingsGlobal.beaconFee) }},
              beaconMaintenanceFee={{ formatCurrencyFromCents(payoutSettingsGlobal.beaconMaintenanceFee) }},
              perDayDriverFee={{ formatCurrencyFromCents(payoutSettingsGlobal.perDayDriverFee) }},
              perKwHFee={{ formatCurrencyFromCents(payoutSettingsGlobal.perKwHFee) }}
            </small>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn flat :disabled="payoutSettingsApiInProgress || !canEditPayoutSettings" @click="resetPayoutSettingsFormToGlobal()">
            Use Global Values
          </v-btn>
          <v-btn flat color="error" :disabled="payoutSettingsApiInProgress || !hasPayoutSettingsOverride || !canEditPayoutSettings" @click="deletePayoutSettingsOverride()">
            Remove Override
          </v-btn>
          <v-spacer />
          <v-btn color="warning" flat :disabled="payoutSettingsApiInProgress" @click="closePayoutSettingsDialog()">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="payoutSettingsApiInProgress || !isPayoutSettingsEdited || !canEditPayoutSettings"
            :loading="payoutSettingsApiInProgress"
            @click="savePayoutSettingsOverride()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <router-view
        :force-root-view-refresh="forceRootViewRefresh"
        :signed-in-user="signedInUser"
        :selected-account="selectedAccount"
        :set-as-selected-account="setAsSelectedAccount"
        :has-user-type="hasUserType"
        :has-transportation-role="hasTransportationRole">
    </router-view>
  </div>
</template>

<script>
import src from './src'
export default src
</script>
